/**
 * Area-level straight-line distance (town/location center → base).
 * Not property-exact, not driving distance.
 */

const KM_TO_MI = 0.621371;

function normalizeLabel(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function haversineKm(lat1, lng1, lat2, lng2) {
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const dPhi = ((lat2 - lat1) * Math.PI) / 180;
  const dLambda = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(dPhi / 2) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return 6371 * c;
}

export function formatDistance(km, unit = 'km') {
  if (!Number.isFinite(km)) return '';
  if (unit === 'mi') {
    const miles = km * KM_TO_MI;
    const rounded = miles >= 10 ? Math.round(miles) : Math.round(miles * 10) / 10;
    return `${rounded} mi`;
  }
  const rounded = km >= 10 ? Math.round(km) : Math.round(km * 10) / 10;
  return `${rounded} km`;
}

export function buildBaseLookup(registry) {
  const bases = registry?.bases || [];
  const byId = new Map();
  const byLabel = new Map();

  bases.forEach((base) => {
    byId.set(base.id, base);
    const labels = [base.name, ...(base.aliases || [])];
    labels.forEach((label) => {
      const key = normalizeLabel(label);
      if (key) byLabel.set(key, base);
    });
  });

  return { byId, byLabel };
}

export function resolveBaseForLabel(label, lookup) {
  if (!label || !lookup) return null;
  return lookup.byLabel.get(normalizeLabel(label)) || null;
}

/**
 * Nearest base among listing.nearby_bases that exist in the registry.
 */
export function findNearestListedBase(listing, registry) {
  const lat = listing?.location_lat;
  const lng = listing?.location_lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const nearby = listing?.nearby_bases || [];
  if (!nearby.length) return null;

  const lookup = buildBaseLookup(registry);
  let nearest = null;

  nearby.forEach((label) => {
    const base = resolveBaseForLabel(label, lookup);
    if (!base || !Number.isFinite(base.lat) || !Number.isFinite(base.lng)) return;
    const km = haversineKm(lat, lng, base.lat, base.lng);
    if (!nearest || km < nearest.distanceKm) {
      nearest = { base, distanceKm: km };
    }
  });

  return nearest;
}

/**
 * Required wording pattern:
 * ~X km from [location_name] area to [base name]
 */
export function formatAreaDistanceLine({ distanceKm, locationName, baseName, unit = 'km' }) {
  if (!Number.isFinite(distanceKm) || !locationName || !baseName) return null;
  const amount = formatDistance(distanceKm, unit);
  if (!amount) return null;
  return `~${amount} from ${locationName} area to ${baseName}`;
}

export function getListingAreaDistanceLine(listing, registry, unit = 'km') {
  const nearest = findNearestListedBase(listing, registry);
  if (!nearest) return null;
  const locationName = listing?.location_name;
  if (!locationName) return null;
  return formatAreaDistanceLine({
    distanceKm: nearest.distanceKm,
    locationName,
    baseName: nearest.base.name,
    unit,
  });
}

/** Area-level estimate disclaimer — not property-exact, not driving time. */
export const AREA_DISTANCE_HELPER_COPY =
  'Distances are estimated from the property\u2019s town or local area. Exact property address may differ.';
